import { skipToken } from '@reduxjs/toolkit/query';
import { RouteCodes } from '@strata/constants';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';

import { UpdateActiveReferralDataPrescriber } from 'components/treatments/dialogs/UpdateActiveReferralDataPrescriber';
import { UpdateActiveReferralDataProductType } from 'components/treatments/dialogs/UpdateActiveReferralDataProductType';
import { UpdateActiveReferralDataSupplier } from 'components/treatments/dialogs/UpdateActiveReferralDataSupplier';
import { handleError } from 'services/Error';
import { usePostDetermineReferralRatesQuery } from 'store/api/rateSetterApi';

const Title = 'Update Active Referral Data';

const UpdateTypes = {
    SUPPLIER: 'Supplier',
    PRESCRIBER: 'Prescriber',
    PRODUCT_TYPE_BRAND: 'Product Type to Brand',
    PRODUCT_TYPE_GENERIC: 'Product Type to Generic',
};
const UpdateTypeComponentMap = {
    [UpdateTypes.SUPPLIER]: UpdateActiveReferralDataSupplier,
    [UpdateTypes.PRESCRIBER]: UpdateActiveReferralDataPrescriber,
    [UpdateTypes.PRODUCT_TYPE_BRAND]: UpdateActiveReferralDataProductType,
    [UpdateTypes.PRODUCT_TYPE_GENERIC]: UpdateActiveReferralDataProductType,
};

export const UpdateActiveReferralDataDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    memberDetails = undefined,
    username,
    submitCallback,
}) => {
    const { payerId, segmentId, productServiceOid, isBrand, placeOfService, activeReferral } =
        treatmentDetails;
    const { benefitType } = activeReferral ?? {};
    const { memberId, firstName } = memberDetails ?? {};

    const { enableGrandfatheredTreatmentActionsOverride = false } = useFlags() ?? {};
    const isGrandfatheredTreatment = !!activeReferral?.grandfathered;
    const isGrandfatheredSupplierOnlyUpdate =
        isGrandfatheredTreatment && enableGrandfatheredTreatmentActionsOverride;

    const [selectedUpdateType, setSelectedUpdateType] = useState();
    const SelectedUpdateTypeComponent = UpdateTypeComponentMap[selectedUpdateType];

    const updateTypeOptions = useMemo(() => {
        if (isGrandfatheredSupplierOnlyUpdate) {
            return [UpdateTypes.SUPPLIER];
        }

        const options = [
            isBrand ? UpdateTypes.PRODUCT_TYPE_GENERIC : UpdateTypes.PRODUCT_TYPE_BRAND,
            UpdateTypes.SUPPLIER,
            UpdateTypes.PRESCRIBER,
        ];
        return options;
    }, [isBrand, isGrandfatheredSupplierOnlyUpdate]);

    // TODO: DEV-14083 Clean up code to pass rate rules to API once API can determine rate rules on backend
    const { data: rateRule } = usePostDetermineReferralRatesQuery(
        treatmentDetails && memberDetails
            ? {
                  payerId,
                  segmentId,
                  productServiceOid,
                  benefit: benefitType,
                  isBrand,
                  memberId,
                  memberFirstName: firstName,
                  memberDateOfBirth: memberDetails.dateOfBirth,
                  routeCode: RouteCodes.ROFR,
                  placeOfService,
                  lineOfBusiness: memberDetails.lineOfBusiness,
              }
            : skipToken,
    );
    const { data: rateRuleOppositeProductType } = usePostDetermineReferralRatesQuery(
        treatmentDetails && memberDetails
            ? {
                  payerId,
                  segmentId,
                  productServiceOid,
                  benefit: benefitType,
                  isBrand: !isBrand, // Show rates for product type referral will be updated to
                  memberId,
                  memberFirstName: firstName,
                  memberDateOfBirth: memberDetails.dateOfBirth,
                  routeCode: RouteCodes.ROFR,
                  placeOfService,
                  lineOfBusiness: memberDetails.lineOfBusiness,
              }
            : skipToken,
    );

    return (
        <Formik
            initialValues={{
                updateType: selectedUpdateType,
                ...SelectedUpdateTypeComponent?.initialValues({ treatmentDetails }),
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                updateType: Yup.string().required(validationStrings.requiredField.errorMessage),
                ...SelectedUpdateTypeComponent?.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await SelectedUpdateTypeComponent?.onSubmit({
                        treatmentDetails,
                        memberDetails,
                        values,
                        username,
                        rateRule,
                        rateRuleOppositeProductType,
                    });

                    submitCallback();

                    SuccessToast({ message: 'Active referral data updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update active referral data. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update Referral"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="updateType"
                                label="What Do You Need To Update?"
                                required
                                values={updateTypeOptions}
                                onChange={setSelectedUpdateType}
                            />
                            {SelectedUpdateTypeComponent && (
                                <SelectedUpdateTypeComponent
                                    treatmentDetails={treatmentDetails}
                                    memberDetails={memberDetails}
                                />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UpdateActiveReferralDataDialog.Title = Title;

UpdateActiveReferralDataDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        payerId: PropTypes.string,
        treatmentId: PropTypes.string,
        isBrand: PropTypes.bool,
        segmentId: PropTypes.string,
        productServiceOid: PropTypes.string,
        placeOfService: PropTypes.string,
        activeReferral: PropTypes.shape({
            benefitType: PropTypes.string,
            grandfathered: PropTypes.bool,
        }),
    }),
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        firstName: PropTypes.string,
        dateOfBirth: PropTypes.string,
    }),
    username: PropTypes.string.isRequired,
    submitCallback: PropTypes.func.isRequired,
};
